/**
 * Frontend logic for Free Shipping Notice block.
 * Works on WooCommerce Cart and Checkout (blocks) pages.
 */
const initFreeShippingNotice = () => {
	const wrappers = document.querySelectorAll(
		'.free-shipping-notice-wrapper, .cc-free-shipping-notice'
	);

	wrappers.forEach( ( noticeWrapper ) => {
		if ( noticeWrapper.dataset.ccFreeShippingBound === 'true' ) {
			return;
		}
		noticeWrapper.dataset.ccFreeShippingBound = 'true';

		const minAmount = parseFloat( noticeWrapper.dataset.minAmount ) || 0;
		const firstOrderCoupon = (
			noticeWrapper.dataset.firstOrderCoupon || ''
		)
			.trim()
			.toLowerCase();
		const messageEl = noticeWrapper.querySelector(
			'.fsn-message, .cc-free-shipping-notice__message'
		);
		const closeBtn = noticeWrapper.querySelector(
			'.fsn-close-btn, .cc-free-shipping-notice__close'
		);

		if ( ! messageEl || ! closeBtn ) {
			return;
		}

		const normalizeCode = ( value ) =>
			String( value || '' )
				.trim()
				.toLowerCase();

		const codesMatch = ( haystack, needle ) => {
			const a = normalizeCode( haystack );
			const b = normalizeCode( needle );
			if ( ! a || ! b ) {
				return false;
			}
			return a === b || a.includes( b ) || b.includes( a );
		};

		const parsePrice = ( priceString ) => {
			const cleaned = String( priceString )
				.replace( /[^\d.]/g, '' )
				.trim();
			return parseFloat( cleaned ) || 0;
		};

		const hideNotice = () => {
			noticeWrapper.style.display = 'none';
		};

		const showNotice = () => {
			noticeWrapper.style.display = '';
		};

		const getCartStore = () => {
			if ( ! window.wp?.data?.select ) {
				return null;
			}

			const storeKey =
				window.wc?.wcBlocksData?.CART_STORE_KEY || 'wc/store/cart';

			try {
				return window.wp.data.select( storeKey );
			} catch ( e ) {
				return null;
			}
		};

		const getStoreCouponCodes = () => {
			const cartStore = getCartStore();
			if ( ! cartStore || typeof cartStore.getCartCoupons !== 'function' ) {
				return null;
			}

			try {
				const coupons = cartStore.getCartCoupons();
				if ( ! Array.isArray( coupons ) ) {
					return null;
				}
				return coupons
					.map( ( coupon ) =>
						normalizeCode( coupon?.code || coupon?.label || '' )
					)
					.filter( Boolean );
			} catch ( e ) {
				return null;
			}
		};

		const getDomCouponCodes = () => {
			const codes = [];

			document
				.querySelectorAll(
					'.wc-block-components-totals-discount__coupon-list-item .wc-block-components-chip__text'
				)
				.forEach( ( el ) => {
					const text = normalizeCode( el.textContent );
					if ( text ) {
						codes.push( text );
					}
				} );

			document
				.querySelectorAll( '.cart-discount[class*="coupon-"]' )
				.forEach( ( row ) => {
					const match = row.className.match( /coupon-([^\s]+)/i );
					if ( match ) {
						codes.push( normalizeCode( match[ 1 ] ) );
					}
				} );

			document
				.querySelectorAll( '.woocommerce-remove-coupon' )
				.forEach( ( el ) => {
					const code =
						el.getAttribute( 'data-coupon' ) ||
						el.dataset?.coupon ||
						'';
					if ( code ) {
						codes.push( normalizeCode( code ) );
					}
				} );

			return codes;
		};

		const listHasFirstOrderCoupon = ( codes ) =>
			Array.isArray( codes ) &&
			codes.some( ( code ) => codesMatch( code, firstOrderCoupon ) );

		/**
		 * Detect whether the configured first-order free-shipping coupon is applied.
		 */
		const isFirstOrderCouponApplied = () => {
			if ( ! firstOrderCoupon ) {
				return noticeWrapper.dataset.firstOrderApplied === '1';
			}

			const storeCodes = getStoreCouponCodes();
			if ( Array.isArray( storeCodes ) ) {
				if ( listHasFirstOrderCoupon( storeCodes ) ) {
					noticeWrapper.dataset.firstOrderApplied = '1';
					return true;
				}

				// Only treat as a definitive "not applied" once the store reports any
				// cart activity (items or coupons), so we don't wipe the SSR flag
				// before hydration.
				let hasCartItems = false;
				try {
					const cartStore = getCartStore();
					const items =
						( typeof cartStore?.getCartItems === 'function' &&
							cartStore.getCartItems() ) ||
						( typeof cartStore?.getCartData === 'function' &&
							cartStore.getCartData()?.items ) ||
						[];
					hasCartItems =
						Array.isArray( items ) && items.length > 0;
				} catch ( e ) {
					hasCartItems = false;
				}

				if ( storeCodes.length > 0 || hasCartItems ) {
					noticeWrapper.dataset.firstOrderApplied = '0';
					return false;
				}
			}

			if ( listHasFirstOrderCoupon( getDomCouponCodes() ) ) {
				noticeWrapper.dataset.firstOrderApplied = '1';
				return true;
			}

			return noticeWrapper.dataset.firstOrderApplied === '1';
		};

		const updateNotice = ( currentPrice ) => {
			showNotice();

			if ( isFirstOrderCouponApplied() ) {
				messageEl.innerHTML =
					'<strong class="fsn-congrats cc-free-shipping-notice__congrats fsn-congrats--first-order">Congratulations! Your first order ships free!</strong>';
				return;
			}

			if ( currentPrice >= minAmount ) {
				messageEl.innerHTML =
					'<strong class="fsn-congrats cc-free-shipping-notice__congrats">Congratulations! Shipping is free!</strong>';
			} else {
				const remaining = ( minAmount - currentPrice ).toFixed( 2 );
				messageEl.innerHTML =
					'Get free shipping if you order <strong class="fsn-remaining cc-free-shipping-notice__remaining">₹' +
					remaining +
					'</strong> more!';
			}
		};

		/**
		 * Prefer Subtotal (pre-coupon). If missing, fall back to Estimated total.
		 */
		const getPriceEl = () =>
			document.querySelector(
				'.wp-block-woocommerce-cart-order-summary-subtotal-block .wc-block-components-totals-item__value'
			) ||
			document.querySelector(
				'.wp-block-woocommerce-checkout-order-summary-subtotal-block .wc-block-components-totals-item__value'
			) ||
			document.querySelector( '.cart-subtotal .amount' ) ||
			document.querySelector(
				'.cart_totals tr.cart-subtotal td[data-title="Subtotal"] .amount'
			) ||
			document.querySelector(
				'.cart-sticky .wc-block-components-totals-footer-item-tax-value'
			) ||
			document.querySelector(
				'.wc-block-components-totals-footer-item-tax-value'
			);

		const handleCartUpdate = () => {
			if ( document.querySelector( '.wc-block-cart__empty' ) ) {
				hideNotice();
				return;
			}

			const priceSpan = getPriceEl();

			if ( ! priceSpan ) {
				if ( isFirstOrderCouponApplied() ) {
					showNotice();
					messageEl.innerHTML =
						'<strong class="fsn-congrats cc-free-shipping-notice__congrats fsn-congrats--first-order">Congratulations! Your first order ships free!</strong>';
					return;
				}
				hideNotice();
				return;
			}

			const rawValue =
				priceSpan.textContent || priceSpan.innerText || '';
			updateNotice( parsePrice( rawValue ) );
		};

		let debounceTimer = null;
		const debouncedHandleCartUpdate = () => {
			if ( debounceTimer ) {
				clearTimeout( debounceTimer );
			}
			debounceTimer = setTimeout( handleCartUpdate, 150 );
		};

		const observer = new MutationObserver( debouncedHandleCartUpdate );
		observer.observe( document.body, {
			childList: true,
			subtree: true,
		} );

		if ( window.wp?.data?.subscribe ) {
			window.wp.data.subscribe( debouncedHandleCartUpdate );
		}

		document.body.addEventListener(
			'wc-blocks_added_to_cart',
			debouncedHandleCartUpdate
		);
		document.body.addEventListener(
			'wc-blocks_removed_from_cart',
			debouncedHandleCartUpdate
		);
		if ( window.jQuery ) {
			window
				.jQuery( document.body )
				.on(
					'applied_coupon removed_coupon updated_cart_totals updated_checkout',
					debouncedHandleCartUpdate
				);
		}

		handleCartUpdate();

		closeBtn.addEventListener( 'click', () => {
			hideNotice();
			observer.disconnect();
		} );
	} );
};

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initFreeShippingNotice );
} else {
	initFreeShippingNotice();
}
